'use client';
import React from 'react';
import styled, { TC } from '@benzinga/themetron';
import { PerformanceColor } from '../PerformanceColor';
import { StockSymbol } from '@benzinga/session';
import { getQuoteChange } from '@benzinga/quotes-v3-manager';
import { QuoteInfo } from '../QuoteInfo';
import { useDetailedQuote, useQuoteSubscription } from '@benzinga/quotes-manager-hooks';
import { useQuoteSubscription as useQuoteSubscriptionV3 } from '@benzinga/quotes-v3-manager-hooks';
import { getTickerDisplayName } from '../../../utils';

interface Props {
  onSymbolClick: (symbol: StockSymbol) => void;
  symbol: StockSymbol;
}

export const TickerHeader: React.FC<Props> = props => {
  const { details: quoteDetail } = useQuoteSubscription(props.symbol);
  const quote = useQuoteSubscriptionV3(props.symbol, new Set(['symbol', 'change', 'changePercent', 'price']));
  const detailedQuote = useDetailedQuote(props.symbol);

  const onSymbolClick = React.useCallback(() => {
    const onSymbolClick = props.onSymbolClick;
    onSymbolClick(props.symbol);
  }, [props.onSymbolClick, props.symbol]);

  const { nominalChange, percentageChange } = getQuoteChange(quote);
  const fullName = detailedQuote?.ok?.description ?? quoteDetail?.companyName ?? undefined;
  const displayName = React.useMemo(
    () => getTickerDisplayName(fullName, detailedQuote?.ok?.name, props.symbol),
    [fullName, detailedQuote?.ok?.name, props.symbol],
  );

  return (
    <TickerTextContainerDiv onClick={onSymbolClick} role="button">
      <TitleHolder>
        <LargeTextDiv title={fullName ?? displayName}>
          {displayName} ({props.symbol})
        </LargeTextDiv>
      </TitleHolder>
      <TickerInfoDiv>{quoteDetail && <QuoteInfo symbol={props.symbol} />}</TickerInfoDiv>
      <TickerPriceContainerRow>
        <TickerPriceDiv>{quote?.price}</TickerPriceDiv>
        <PerformanceContainerRow>
          <PerformanceColorPaddingDiv>
            <PerformanceColor hasArrow stat={nominalChange} />
          </PerformanceColorPaddingDiv>{' '}
          <PerformanceColor isPercentage stat={percentageChange} />
        </PerformanceContainerRow>
      </TickerPriceContainerRow>
    </TickerTextContainerDiv>
  );
};

const TitleHolder = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LargeTextDiv = styled.div`
  font-size: 1.5em;
  font-weight: 500;
  margin-bottom: 4px;
  min-width: 0;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TickerPriceDiv = styled.div`
  display: inline-block;
  font-size: 1.5em;
  font-weight: 500;
  margin-right: 0.25em;
`;

const TickerInfoDiv = styled.div`
  font-weight: 300;
  font-size: 0.8em;
  white-space: nowrap;
`;

const TickerPriceContainerRow = styled(TC.Row)`
  align-items: baseline;
  padding-top: 5px;
`;

const PerformanceContainerRow = styled(TC.Row)`
  font-size: 1em;
  white-space: nowrap;
`;

const TickerTextContainerDiv = styled(TC.Div)`
  min-width: 0;
  max-width: 100%;
  padding: 10px 12px;
`;

const PerformanceColorPaddingDiv = styled.div`
  padding-right: 0.25em;
`;
